import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListChecks, ClipboardList, Bell, FileText, MousePointerClick, Inbox, Table2, Layers } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useWorktableContext } from '@/lib/worktableContext';
import { eventLog } from '@/lib/eventLog';
import { AutoListForm } from '@/components/autoforms/AutoListForm';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { ObjectCard, type CardField } from '@/components/ui/ObjectCard';
import { CalendarWidget } from '@/components/widgets/CalendarWidget';
import { CardGrid } from '@/components/ui/CardGrid';
import { Button } from '@/components/ui/button';
import { registerWidget, listWidgets, type WidgetContext, type WidgetProps } from './registry';
import { QuickAccessHubWidget } from './quickAccessHub';
import { registerSiteBlocks } from './blocks';
import { registerSiteMarketplaceBlocks } from './blocks-marketplace';
import { SitePageRecords, siteQueryFilters } from './sitePageRecords';
import type { WorktableWidget } from './gridModel';
import { friendlyError } from '@/lib/errors';
import type { ListFilter } from '@/types/formProperties';
import { resolveDlFilters } from '@/lib/dlFilters';
import { ChatThread } from '@/components/chat/ChatThread';
import { TabBar } from '@/components/ui/TabBar';
import { useBreakpoint } from '@/hooks/useBreakpoint';

// ============================================================================
// lib/widgets/builtin.tsx — СТАНДАРТНЫЕ ВИДЖЕТЫ ЯДРА (Рабочие столы + Страницы).
// SSoT — сервер: данные через SDK (TaskService/OrderService/NotificationService/
// ListTableData); клиент только рендерит. Ошибки ЯВНЫЕ (без тихих фоллбэков).
// ============================================================================

// --- Общие helpers ----------------------------------------------------------

import {
  WidgetShell, EmptyState, TasksMineWidget, OrdersMineWidget, TasksPoolWidget,
  NotificationsWidget, QuickActionsWidget, TextWidget, HtmlWidget, ListFormWidget,
  ContextWidget, ChatThreadWidget,
} from './builtinWidgets';
export function registerBuiltinWidgets(): void {
  registerWidget({
    type: 'calendar',
    display: 'Календарь',
    description: 'События и сроки задач (день/неделя) — единый календарь платформы',
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Календарь' },
      { key: 'viewDays', label: 'Дней на экране', type: 'number', placeholder: '7' },
      { key: 'mine', label: 'Только мои (по исполнителю)', type: 'boolean' },
    ],
    render: CalendarWidget as never,
  });
  registerWidget({
    type: 'tasks_mine',
    display: 'Мои задачи',
    description: 'Список моих задач (карточки)',
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Мои задачи' },
      { key: 'limit', label: 'Сколько показать', type: 'number', placeholder: '8' },
    ],
    render: TasksMineWidget,
  });
  registerWidget({
    type: 'orders_mine',
    display: 'Мои заявки',
    description: 'Список моих заявок (карточки)',
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Мои заявки' },
      { key: 'limit', label: 'Сколько показать', type: 'number', placeholder: '8' },
    ],
    render: OrdersMineWidget,
  });
  registerWidget({
    type: 'tasks_pool',
    display: 'Пул задач',
    description: 'Задачи, доступные для взятия',
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Пул задач' },
      { key: 'limit', label: 'Сколько показать', type: 'number', placeholder: '8' },
    ],
    render: TasksPoolWidget,
  });
  registerWidget({
    type: 'notifications',
    display: 'Уведомления',
    description: 'Лента уведомлений',
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Уведомления' },
      { key: 'limit', label: 'Сколько показать', type: 'number', placeholder: '8' },
    ],
    render: NotificationsWidget,
  });
  registerWidget({
    type: 'quick_actions',
    display: 'Быстрые действия',
    description: 'Кнопки-команды',
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Быстрые действия' },
      { key: 'actions', label: 'Действия (label;команда — по одной в строке)', type: 'text', placeholder: 'Новая заявка;navigate:/orders\nМои задачи;navigate:/tasks' },
    ],
    render: QuickActionsWidget,
  });
  registerWidget({ type: 'text', display: 'Текст', description: 'Статичный текст', publicOk: true,
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Заголовок' },
      { key: 'text', label: 'Текст', type: 'text', placeholder: 'Содержимое' },
    ],
    render: TextWidget });
  registerWidget({ type: 'html', display: 'HTML', description: 'HTML-блок', publicOk: true,
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Заголовок' },
      { key: 'text', label: 'HTML', type: 'text', placeholder: '<b>Контент</b>' },
    ],
    render: HtmlWidget });
  registerWidget({
    type: 'list',
    display: 'Форма списка',
    description: 'Стандартная форма списка объекта с отборами и видом',
    // Публичные сайты: список рендерится анониму по ГОСТЕВЫМ правам
    // (сервер: роль «Гость» — только таблицы с явной записью прав).
    publicOk: true,
    propsSchema: [
      { key: 'objectType', label: 'Тип объекта', type: 'select',
        options: [
          { value: 'catalog', label: 'Справочник' },
          { value: 'document', label: 'Документ' },
          { value: 'order', label: 'Заявка' },
          { value: 'task', label: 'Задача' },
          { value: 'register', label: 'Регистр' },
          { value: 'poll', label: 'Тест/анкета' },
        ] },
      { key: 'objectName', label: 'Объект', type: 'objects', placeholder: 'orders' },
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Мои заявки' },
      { key: 'filters', label: 'Отборы', type: 'filters', help: '@current_user / @context.имя / @param.имя' },
      { key: 'params', label: 'Параметры виджета', type: 'params', help: 'в отборах: @param.имя' },
      { key: 'contextParam', label: 'Параметр контекста (master)', type: 'text', placeholder: 'Контрагент', help: 'клик по строке → @context.<имя> для «slave»-виджетов' },
      { key: 'contextField', label: 'Поле для контекста', type: 'text', placeholder: 'id' },
      { key: 'viewMode', label: 'Вид', type: 'select',
        options: [
          { value: 'list', label: 'Список' },
          { value: 'cards', label: 'Карточки' },
          { value: 'tree', label: 'Дерево' },
          { value: 'flat', label: 'Плоский список' },
        ] },
      { key: 'groupMode', label: 'Группировка', type: 'select',
        options: [
          { value: '', label: 'Без группировки' },
          { value: 'sections', label: 'Секции (сворачиваемые)' },
          { value: 'kanban', label: 'Канбан (колонки)' },
          { value: 'chips', label: 'Чипы-итоги (фильтр)' },
          { value: 'cards', label: 'Карточки-группы (с провалом)' },
        ] },
      { key: 'groupBy', label: 'Поля группировки', type: 'text', placeholder: 'status_code,assignee_id', help: 'через запятую; порядок = вложенность для «карточек-групп» (status_code — по-русски)' },
      { key: 'showToolbar', label: 'Панель команд', type: 'boolean' },
      { key: 'commands', label: 'Команды', type: 'multi', options: [
        { value: 'create', label: 'Создать' },
        { value: 'create_group', label: 'Добавить группу' },
        { value: 'refresh', label: 'Обновить' },
        { value: 'delete', label: 'Удалить' },
        { value: 'search', label: 'Поиск' },
        { value: 'filter', label: 'Отбор' },
      ], help: 'Пусто = все команды' },
      { key: 'listTitle', label: 'Заголовок списка', type: 'boolean', help: 'отдельный заголовок над таблицей' },
      { key: 'listBorder', label: 'Рамка списка', type: 'boolean', help: 'рамка вокруг таблицы' },
      // Хвосты Этапов 2–3 (сайты): записи страницы-списка + query-отборы.
      { key: 'pageRecords', label: 'Записи страницы-списка (серверная пагинация)', type: 'boolean',
        help: 'на странице объекта (режим «Список»): записи и пагинация с сервера; карточка ведёт на item-страницу' },
      { key: 'queryFields', label: 'Поля отбора из URL (через запятую)', type: 'text', placeholder: 'category,status_code',
        help: 'whitelist query-параметров ?поле=значение; сервер фильтрует только эти поля' },
      { key: 'itemUrl', label: 'URL-шаблон записи', type: 'text', placeholder: '/p/{site}/{slug_prefix}/{ref}',
        help: 'токены {ref}/{site}/{locale}/{slug_prefix}; пусто — канонический URL сайта' },
      // Кабинеты (Этап 3): отбор @current_user у анонима → заглушка «Войдите»
      // (снять галочку — блок скрывается целиком).
      { key: 'guestStub', label: 'Заглушка «Войдите» для гостя', type: 'boolean', help: 'для отборов @current_user на публичном сайте' },
    ],
    render: ListFormWidget,
  });
  registerWidget({
    type: 'context',
    display: 'Контекст (параметр)',
    description: 'Поле ввода параметра контекста рабочего стола (master→slave)',
    propsSchema: [
      { key: 'param', label: 'Имя параметра контекста', type: 'text', placeholder: 'Контрагент', help: 'значение уйдёт в @context.<имя> — обновит отборы всех виджетов' },
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Контекст: Контрагент' },
      { key: 'placeholder', label: 'Подсказка ввода', type: 'text', placeholder: 'значение параметра…' },
    ],
    render: ContextWidget,
  });
  registerWidget({
    type: 'chat_thread',
    display: 'Чат',
    description: 'Встраиваемый чат (как Telegram) — переписка выбранного диалога',
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Чат' },
      { key: 'conversationId', label: 'ID чата (пусто — первый)', type: 'text', placeholder: 'uuid чата' },
    ],
    render: ChatThreadWidget,
  });
  registerWidget({
    type: 'group',
    display: 'Группа',
    description: 'Контейнер для виджетов (как группа в форме): вертикальная/горизонтальная раскладка',
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Группа' },
      { key: 'direction', label: 'Раскладка', type: 'select', options: [
        { value: 'vertical', label: 'Вертикальная' },
        { value: 'horizontal', label: 'Горизонтальная' },
      ] },
    ],
    render: GroupWidget,
  });
  registerWidget({
    type: 'quick_access_hub',
    display: 'Быстрый доступ (карточки разделов)',
    description: 'Карточки доступных разделов и быстрых действий с навигацией; закрепление и недавние',
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text', placeholder: 'Быстрый доступ' },
      { key: 'cardCount', label: 'Сколько карточек', type: 'number', placeholder: '12' },
      { key: 'search', label: 'Показывать поиск', type: 'boolean' },
      { key: 'source', label: 'Источник набора', type: 'select', options: [
        { value: 'auto', label: 'Авто (закреплённые + недавние + разделы)' },
        { value: 'all', label: 'Все разделы' },
        { value: 'pinned', label: 'Только закреплённые' },
      ] },
    ],
    render: QuickAccessHubWidget,
  });
  // Блоки САЙТОВ (Этап 1): heading/text/image/button/divider/spacer/hero
  // (siteOk — палитра конструктора сайтов; appearance + локализация).
  registerSiteBlocks();
  // Блоки ИНТЕРАКТИВА (Этап 3): form/reviews/review_form/rating/search/facets.
  registerSiteMarketplaceBlocks();
}

// --- «Группа» (контейнер виджетов, как группа в форме) -----------------------
// Рендерит ВЛОЖЕННЫЕ виджеты (children) вертикально/горизонтально.
// В редакторе рабочего стола виджеты добавляются внутрь группы.
// Мобильный: горизонтальная группа — ВКЛАДКИ (GroupTabs — отдельный компонент,
// чтобы хуки GroupWidget оставались СТАБИЛЬНЫМИ при любом ресайзе: сам GroupWidget
// всегда вызывает ровно useBreakpoint+useState; вкладки/активный виджет — внутри
// GroupTabs. Это исключает «React error #310» от смены числа хуков).
function GroupWidget(props: WidgetProps, ctx: WidgetContext) {
  const children = Array.isArray((props as any).children) ? (props as any).children as WorktableWidget[] : [];
  const direction = String((props as any).direction || 'vertical');
  const { isMobile } = useBreakpoint();
  const effective = isMobile && direction === 'horizontal' ? 'tabs' : direction;
  return (
    <WidgetShell title={props.title || 'Группа'} showTitle={props.showTitle} showBorder={props.showBorder} icon={<Layers className="h-4 w-4" />}>
      {children.length === 0 ? (
        <EmptyState text="Группа пуста — добавьте виджеты внутрь (в редакторе рабочего стола)" />
      ) : (
        <GroupTabs key={effective} children={children} effective={effective} ctx={ctx} />
      )}
    </WidgetShell>
  );
}

function GroupTabs({ children, effective, ctx }: { children: WorktableWidget[]; effective: string; ctx: WidgetContext }) {
  const [activeChild, setActiveChild] = useState(0);
  const visible = effective === 'tabs' ? [children[Math.min(activeChild, children.length - 1)]].filter(Boolean) : children;
  return (
    <div className="flex flex-col flex-1 min-h-0">
      {effective === 'tabs' && children.length > 1 && (
        <div className="shrink-0 px-1 pt-1">
          <TabBar
            tabs={children.map((cw, i) => ({ id: String(i), label: String((cw.props as any)?.title || listWidgets().find((x) => x.type === cw.widget_type)?.display || 'Виджет') }))}
            active={String(Math.min(activeChild, children.length - 1))}
            onChange={(id) => setActiveChild(Number(id))}
          />
        </div>
      )}
      <div className={`flex-1 min-h-0 flex ${effective === 'tabs' ? 'flex-col p-1' : effective === 'horizontal' ? 'flex-row gap-2 p-2' : 'flex-col gap-2 p-2'} overflow-auto`}>
        {visible.map((cw) => {
          const def = listWidgets().find((x) => x.type === cw.widget_type);
          if (!def) return <div key={cw.id} className="rounded border border-dashed p-2 text-[11px] text-destructive">Виджет «{cw.widget_type}» не поддерживается</div>;
          return (
            <div key={cw.id} className={`flex-1 min-h-0 min-w-0 ${effective === 'horizontal' ? 'w-1/2' : ''}`}>
              <div className="h-full">
                {def.render({ ...(cw.props || {}), showTitle: effective === 'tabs' ? false : (cw.props as any)?.showTitle, children: cw.children }, ctx)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
